import { html, SP } from '../html.js';
import type { VNode } from '../html.js';
import { splitDetail } from '../parse.js';
import type { Clearance, StageState } from '../types.js';

/**
 * The clearance ledger — the console's one memorable element.
 *
 * A manifest is a cargo declaration and the probe stage is customs: every
 * proposed capability is executed against the live origin and either clears or
 * is turned back with a stamped reason. That refusal is the most trust-building
 * thing the product does, so stage 5 is not drawn like stages 1–4. It gets its
 * own framed block, a tally that counts against what synthesize DECLARED, and a
 * row per capability that is present from the moment it is proposed — waiting,
 * then stamped. A capability never appears out of nowhere and the operator can
 * always tell a gate that has finished from a gate that has not started.
 */

function duration(stage: StageState, now: number): string | null {
  if (stage.startedAt == null) return null;
  const end = stage.endedAt ?? (stage.status === 'running' ? now : null);
  if (end == null) return null;
  const ms = Math.max(0, end - stage.startedAt);
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function Note(note: string): VNode {
  const { label, value } = splitDetail(note);
  return html`<li class="stage__note">${label ? html`<b>${label}</b>` : null}${value}</li>`;
}

export function ClearanceRow(c: Clearance): VNode {
  const cleared = c.verdict === 'cleared';
  return html`
    <li class=${cleared ? 'clr' : 'clr clr--turned'}>
      <span class=${cleared ? 'stamp stamp--cleared' : 'stamp stamp--turned'}>
        ${cleared ? 'Cleared' : 'Turned back'}
      </span>
      <span class="clr__id">${c.id}</span>
      <span class="clr__rows">
        ${c.rows} ${c.rows === 1 ? 'row' : 'rows'}${c.samples ? ` · ${c.samples}×` : ''}
      </span>
      ${c.reason ? html`<span class="clr__why">${c.reason}</span>` : null}
    </li>
  `;
}

/** A capability that has been declared but not yet executed. */
function WaitingRow(id: string): VNode {
  return html`
    <li class="clr clr--waiting">
      <span class="stamp stamp--waiting">In the queue</span>
      <span class="clr__id">${id}</span>
      <span class="clr__rows">not executed yet</span>
    </li>
  `;
}

export function Clearances(props: { rows: Clearance[]; standalone?: boolean }): VNode | null {
  if (!props.rows.length) return null;
  return html`
    <ul class=${props.standalone ? 'clearances clearances--standalone' : 'clearances'}>
      ${props.rows.map((c) => ClearanceRow(c))}
    </ul>
  `;
}

/**
 * Stage 5, drawn as customs.
 *
 * The bar is not a progress bar. Its three segments are counts of real
 * verdicts — cleared, turned back, still queued — so it can only move when the
 * prober actually answers. Nothing here animates ahead of the data.
 */
function Customs(props: {
  proposals: string[];
  clearances: Clearance[];
  status: StageState['status'];
}): VNode {
  const { proposals, clearances } = props;
  const cleared = clearances.filter((c) => c.verdict === 'cleared');
  const turned = clearances.filter((c) => c.verdict === 'turned-back');
  const judged = new Set(clearances.map((c) => c.id));
  const waiting = proposals.filter((id) => !judged.has(id));
  const declared = Math.max(proposals.length, clearances.length);

  if (props.status === 'waiting') return html`<p class="customs__idle">Waiting for stage 4.</p>`;

  if (declared === 0) {
    return html`
      <div class="customs customs--barren">
        <p class="customs__barren">
          <b>Nothing was declared.</b> Stage 4 proposed no capability, so the gate had nothing to
          execute. No tool was verified because no tool was invented to verify.
        </p>
      </div>
    `;
  }

  const pc = (n: number): string => `${(n / declared) * 100}%`;

  return html`
    <div class="customs">
      <div class="customs__head">
        <span class="customs__title">The gate</span>
        <span class="customs__tally">
          <b>${declared}</b> declared
          <i class="customs__sep" aria-hidden="true">${SP}·${SP}</i>
          <b class="is-cleared">${cleared.length}</b> cleared
          <i class="customs__sep" aria-hidden="true">${SP}·${SP}</i>
          <b class="is-turned">${turned.length}</b> turned back
          ${waiting.length
            ? html`<i class="customs__sep" aria-hidden="true">${SP}·${SP}</i>
                <b>${waiting.length}</b> in the queue`
            : null}
        </span>
      </div>

      <div class="customs__bar" aria-hidden="true">
        <span class="customs__seg customs__seg--cleared" style=${`width:${pc(cleared.length)}`}></span>
        <span class="customs__seg customs__seg--turned" style=${`width:${pc(turned.length)}`}></span>
      </div>

      <ul class="clearances clearances--inset">
        ${clearances.map((c) => ClearanceRow(c))} ${waiting.map((id) => WaitingRow(id))}
      </ul>

      ${props.status === 'done' && turned.length
        ? html`<p class="customs__foot">
            ${turned.length} of ${declared} did not survive execution and will not be published.
            Each reason above is the prober's own, verbatim.
          </p>`
        : null}
    </div>
  `;
}

/** Stage 4's output, as declarations rather than as log lines. */
function Declared(props: { proposals: string[] }): VNode | null {
  if (!props.proposals.length) return null;
  return html`
    <ul class="declared" aria-label="Capabilities proposed">
      ${props.proposals.map((id) => html`<li class="declared__item">${id}</li>`)}
    </ul>
  `;
}

export function Ledger(props: {
  stages: StageState[];
  proposals: string[];
  clearances: Clearance[];
  now: number;
}): VNode {
  return html`
    <ol class="ledger" aria-label="Crawl stages">
      ${props.stages.map((stage) => {
        const time = duration(stage, props.now);
        const isProbe = stage.key === 'probe';
        return html`
          <li class=${`stage stage--${stage.status}${isProbe ? ' stage--gate' : ''}`}>
            <span class="stage__ord">${stage.ordinal}/6</span>
            <div>
              <div class="stage__head">
                <span class="stage__name"><i class="pip" aria-hidden="true"></i>${stage.label}</span>
                <span class="stage__gloss">${stage.gloss}</span>
                ${time ? html`<span class="stage__time">${time}</span>` : null}
              </div>
              ${!isProbe && stage.notes.length
                ? html`<ul class="stage__notes">
                    ${stage.notes.map((n) => Note(n))}
                  </ul>`
                : null}
              ${stage.key === 'synthesize' ? Declared({ proposals: props.proposals }) : null}
              ${isProbe
                ? Customs({
                    proposals: props.proposals,
                    clearances: props.clearances,
                    status: stage.status,
                  })
                : null}
            </div>
          </li>
        `;
      })}
    </ol>
  `;
}
